import * as React from 'react'
import { Check, Loader2 } from 'lucide-react'
import type {
  HcpServiceDto,
  HousecallProStatusDto,
} from '@lib/dto/housecall-pro.dto'
import {
  useHcpServices,
  useHousecallProStatus,
  useUpdateHousecallPro,
} from '@/hooks/useHousecallPro'
import { HcpStatusMessage } from '@/components/HcpStatusMessage'
import { Button } from '@/components/ui/button'
import { SELECT_CLASS } from '@/components/ui/field-classes'
import { errorMessage } from '@/helpers/logger'

/**
 * The services worth offering for tree work: anything named or filed as tree
 * work, plus the ones already chosen, so a mapping to an oddly named service
 * still shows.
 */
function treeOptions(
  services: readonly HcpServiceDto[],
  chosen: readonly (string | null)[],
): HcpServiceDto[] {
  return services.filter(
    (service) =>
      /tree/i.test(service.name) ||
      /tree/i.test(service.categoryName ?? '') ||
      chosen.includes(service.uuid),
  )
}

/**
 * Which Housecall Pro services a tree job's lines are made on.
 *
 * A tree job from an accepted proposal has one line per tree, at the price the
 * client accepted, as the office writes its own tree jobs: a tree being pruned
 * goes on the pruning service with its treatments in the price, and one coming
 * down on the removal service. Nothing is shown until HCP is connected.
 */
export function TreeJobSettings() {
  const status = useHousecallProStatus()
  // Held here, above the key: a save remounts the form, and "Saved" has to
  // outlive that.
  const [saved, setSaved] = React.useState(false)
  if (status.isPending || status.isError) {
    return <HcpStatusMessage status={status} />
  }
  if (!status.data.configured) return null
  const stored = status.data
  return (
    <TreeJobForm
      // A save that changes what is stored starts the form again from it.
      key={`${stored.treePruningService?.uuid ?? ''}|${stored.treeRemovalService?.uuid ?? ''}`}
      status={stored}
      saved={saved}
      onSavedChange={setSaved}
    />
  )
}

interface TreeJobFormProps {
  status: HousecallProStatusDto
  /** Whether the last save went through and nothing has changed since. */
  saved: boolean
  onSavedChange: (saved: boolean) => void
}

function TreeJobForm({ status, saved, onSavedChange }: TreeJobFormProps) {
  const services = useHcpServices(true)
  const update = useUpdateHousecallPro()
  const [pruning, setPruning] = React.useState<string | null>(
    status.treePruningService?.uuid ?? null,
  )
  const [removal, setRemoval] = React.useState<string | null>(
    status.treeRemovalService?.uuid ?? null,
  )
  const [error, setError] = React.useState<string | null>(null)

  const options = treeOptions(services.data?.services ?? [], [pruning, removal])
  const isDirty =
    pruning !== (status.treePruningService?.uuid ?? null) ||
    removal !== (status.treeRemovalService?.uuid ?? null)

  async function handleSave(): Promise<void> {
    setError(null)
    try {
      await update.mutateAsync({
        treePruningServiceUuid: pruning,
        treeRemovalServiceUuid: removal,
      })
      onSavedChange(true)
    } catch (caught) {
      setError(errorMessage(caught, 'That could not be saved.'))
    }
  }

  function serviceSelect(
    id: string,
    label: string,
    value: string | null,
    onChange: (uuid: string | null) => void,
  ): React.ReactNode {
    return (
      <div className="space-y-1">
        <label htmlFor={id} className="text-sm font-medium">
          {label}
        </label>
        <select
          id={id}
          className={SELECT_CLASS}
          value={value ?? ''}
          disabled={update.isPending || services.isPending}
          onChange={(event) => {
            onSavedChange(false)
            onChange(event.target.value === '' ? null : event.target.value)
          }}
        >
          <option value="">Not chosen</option>
          {options.map((service) => (
            <option key={service.uuid} value={service.uuid}>
              {service.name}
              {service.taxable ? ' · taxed' : ''}
            </option>
          ))}
        </select>
      </div>
    )
  }

  return (
    <section className="space-y-3 rounded-lg border border-default bg-surface p-4">
      <div>
        <h2 className="text-lg font-semibold">Tree jobs in Housecall Pro</h2>
        <p className="text-sm text-secondary-text">
          A tree job made from an accepted proposal has one line per tree, at
          the price the client accepted: a tree being pruned on the pruning
          service, its treatments included, and one coming down on the removal
          service.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {serviceSelect('hcp-tree-pruning', 'Pruning', pruning, setPruning)}
        {serviceSelect('hcp-tree-removal', 'Removal', removal, setRemoval)}
      </div>

      {services.isError ? (
        <p className="text-sm text-danger">{errorMessage(services.error, 'That could not be saved.')}</p>
      ) : null}
      {error === null ? null : <p className="text-sm text-danger">{error}</p>}

      <div className="flex items-center gap-3 border-t border-default pt-3">
        <Button
          disabled={!isDirty || update.isPending}
          onClick={() => {
            void handleSave()
          }}
        >
          {update.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : null}
          Save changes
        </Button>
        {saved && !isDirty ? (
          <span className="flex items-center gap-1 text-sm text-muted-text">
            <Check className="h-3.5 w-3.5" />
            Saved
          </span>
        ) : null}
      </div>
    </section>
  )
}
